import type { RestaurantWorkspace } from '../types/workspace';
import { DEFAULT_CURRENCY_CODE, DEFAULT_CURRENCY_SYMBOL } from './restaurantBranding';

const IMAGE_REFERENCE_PATTERN = /^(https?:\/\/|\/|data:image\/|blob:)/i;

const ABSOLUTE_URL_PATTERN = /^https?:\/\/.+/i;
const HEX_COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const collectDuplicateKeys = (values: string[]) => {
  const counts = new Map<string, number>();
  values.forEach((value) => {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return;
    }
    counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
  });

  return [...counts.entries()].filter(([, count]) => count > 1).map(([value]) => value);
};

export const isImageReference = (value: string) => IMAGE_REFERENCE_PATTERN.test(value.trim());

export interface WorkspaceAudit {
  categoriesWithoutCoverImage: number;
  categoriesWithoutVisibleItems: number;
  duplicateCategorySlugs: string[];
  duplicateItemSlugs: string[];
  itemsHidden: number;
  itemsUnavailable: number;
  itemsWithoutDescription: number;
  itemsWithoutImage: number;
  settingsMissing: string[];
}

export const auditWorkspace = (workspace: RestaurantWorkspace): WorkspaceAudit => {
  const visibleItemsByCategoryId = new Map<string, number>();

  workspace.items.forEach((item) => {
    if (!item.isVisible) {
      return;
    }
    visibleItemsByCategoryId.set(item.categoryId, (visibleItemsByCategoryId.get(item.categoryId) ?? 0) + 1);
  });

  return {
    categoriesWithoutCoverImage: workspace.categories.filter((category) => !category.coverImage.trim()).length,
    categoriesWithoutVisibleItems: workspace.categories.filter(
      (category) => (visibleItemsByCategoryId.get(category.id) ?? 0) === 0,
    ).length,
    duplicateCategorySlugs: collectDuplicateKeys(workspace.categories.map((category) => category.slug)),
    duplicateItemSlugs: collectDuplicateKeys(workspace.items.map((item) => item.slug)),
    itemsHidden: workspace.items.filter((item) => !item.isVisible).length,
    itemsUnavailable: workspace.items.filter((item) => !item.isAvailable).length,
    itemsWithoutDescription: workspace.items.filter((item) => !item.description.trim()).length,
    itemsWithoutImage: workspace.items.filter((item) => !item.imageUrl.trim()).length,
    settingsMissing: [
      !workspace.settings.name.trim() ? 'Restaurant name' : null,
      !workspace.settings.phoneNumber.trim() ? 'Phone number' : null,
      !workspace.settings.location.trim() ? 'Location' : null,
      !workspace.settings.coverImage.trim() ? 'Main cover image' : null,
      !workspace.settings.menuUrl.trim() ? 'Menu URL' : null,
      !workspace.settings.logoImage.trim() ? 'Logo image' : null,
      !workspace.settings.faviconImage.trim() ? 'Tab icon' : null,
    ].filter((value): value is string => Boolean(value)),
  };
};

export const getWorkspaceValidationErrors = (workspace: RestaurantWorkspace): string[] => {
  const errors: string[] = [];
  const audit = auditWorkspace(workspace);

  if (!workspace.settings.name.trim()) {
    errors.push('Restaurant name is required.');
  }

  if (!workspace.settings.phoneNumber.trim()) {
    errors.push('Phone number is required.');
  }

  if (!workspace.settings.coverImage.trim()) {
    errors.push('A main cover image is required.');
  } else if (!isImageReference(workspace.settings.coverImage)) {
    errors.push('The main cover image must be a valid uploaded image or image URL.');
  }

  if (!workspace.settings.menuUrl.trim()) {
    errors.push('Menu URL is required.');
  } else if (!ABSOLUTE_URL_PATTERN.test(workspace.settings.menuUrl.trim())) {
    errors.push('Menu URL must be a full http or https address.');
  }

  if (!workspace.settings.logoImage.trim()) {
    errors.push('A logo image is required.');
  } else if (!isImageReference(workspace.settings.logoImage)) {
    errors.push('The logo image must be a valid uploaded image or image URL.');
  }

  if (!workspace.settings.faviconImage.trim()) {
    errors.push('A tab icon image is required.');
  } else if (!isImageReference(workspace.settings.faviconImage)) {
    errors.push('The tab icon must be a valid uploaded image or image URL.');
  }

  if (workspace.settings.mapsUrl.trim() && !ABSOLUTE_URL_PATTERN.test(workspace.settings.mapsUrl.trim())) {
    errors.push('Map URL must be a full http or https address.');
  }

  if (!workspace.settings.currencyCode.trim()) {
    errors.push('Currency code is required.');
  }

  if (!workspace.settings.currencySymbol.trim()) {
    errors.push('Currency symbol is required.');
  }

  if (!workspace.settings.callToActionLabel.trim()) {
    errors.push('Call button label is required.');
  }

  const brandColors = workspace.settings.brandColors;
  Object.entries(brandColors).forEach(([key, value]) => {
    if (!HEX_COLOR_PATTERN.test(value.trim())) {
      errors.push(`Brand color "${key}" must be a valid hex color.`);
    }
  });

  if (
    workspace.settings.currencyCode.trim() === DEFAULT_CURRENCY_CODE &&
    !workspace.settings.currencySymbol.trim()
  ) {
    errors.push('Currency symbol is required when currency code is set.');
  }

  if (
    workspace.settings.currencySymbol.trim() === DEFAULT_CURRENCY_SYMBOL &&
    !workspace.settings.currencyCode.trim()
  ) {
    errors.push('Currency code is required when currency symbol is set.');
  }

  if (audit.duplicateCategorySlugs.length > 0) {
    errors.push(`Category slugs must be unique. Duplicate slug(s): ${audit.duplicateCategorySlugs.join(', ')}.`);
  }

  if (audit.duplicateItemSlugs.length > 0) {
    errors.push(`Item slugs must be unique. Duplicate slug(s): ${audit.duplicateItemSlugs.join(', ')}.`);
  }

  workspace.categories.forEach((category) => {
    if (!category.title.trim()) {
      errors.push('Every category needs a title.');
    }
    if (!category.coverImage.trim()) {
      errors.push(`Category "${category.title || 'Untitled'}" needs a cover image.`);
    } else if (!isImageReference(category.coverImage)) {
      errors.push(`Category "${category.title || 'Untitled'}" must use a valid cover image path or URL.`);
    }
  });

  const validCategoryIds = new Set(workspace.categories.map((category) => category.id));

  workspace.items.forEach((item) => {
    if (!item.name.trim()) {
      errors.push('Every item needs a name.');
    }
    if (!validCategoryIds.has(item.categoryId)) {
      errors.push(`Item "${item.name || 'Untitled'}" is assigned to a missing category.`);
    }
    if (!Number.isFinite(item.price)) {
      errors.push(`Item "${item.name || 'Untitled'}" needs a valid price.`);
    }
    if (item.imageUrl.trim() && !isImageReference(item.imageUrl)) {
      errors.push(`Item "${item.name || 'Untitled'}" must use a valid image path or URL.`);
    }
  });

  return [...new Set(errors)];
};
